/**
 * Decision 0086 (build map §6.1): the pure side of Knowledge, the workspace's own place for people pages and
 * guidelines. Paths, search params, names, bylines and the "not filled in yet" rule. No React.
 */
import {
  type Actor,
  CAPABILITIES_PAGE_PATH,
  PERSON_SECTIONS,
  type PersonFrontmatter,
  personHandleOf,
  WORKSPACE_TOPIC,
  type WorkspaceKnowledgeEntry,
  type WorkspacePageVersion,
  workspaceTopicOf,
} from "@minga/protocol"
import { formatRelative } from "@/lib/time"

/** Decision 0100: Runners holds `runners/capabilities.md`, what works where. */
export type KnowledgeTab = "people" | "guidelines" | "runners"

export interface KnowledgeSearch {
  readonly tab?: KnowledgeTab
  readonly q?: string
}

/** `/knowledge?tab=guidelines&q=…`: People is the default tab; `q` is the search box (up to 200 characters). */
export const validateKnowledgeIndexSearch = (search: Record<string, unknown>): KnowledgeSearch => ({
  ...(search.tab === "guidelines" || search.tab === "people" || search.tab === "runners"
    ? { tab: search.tab }
    : {}),
  ...(typeof search.q === "string" && search.q.trim() ? { q: search.q.slice(0, 200) } : {}),
})

/** `?v=<n>` on a page: an older version. */
export const validateVersionSearch = (search: Record<string, unknown>): { v?: number } => {
  const v =
    typeof search.v === "number" ? search.v : typeof search.v === "string" ? Number(search.v) : Number.NaN
  return Number.isInteger(v) && v > 0 && v < 1_000_000 ? { v } : {}
}

const enc = encodeURIComponent

/** The app path of Knowledge. */
export const knowledgePath = (workspaceId: string, tab?: KnowledgeTab): string =>
  `/w/${enc(workspaceId)}/knowledge${tab && tab !== "people" ? `?tab=${tab}` : ""}`

/** The index's search params for a tab (People is the default and has none). */
export const tabSearch = (tab: KnowledgeTab): { tab?: KnowledgeTab } => (tab === "people" ? {} : { tab })

/** Decision 0100: the app path of the capabilities page. */
export const capabilitiesPagePathFor = (workspaceId: string, v?: number): string =>
  `/w/${enc(workspaceId)}/knowledge/runners/capabilities${v ? `?v=${v}` : ""}`

/** Decision 0100: is this workspace path the capabilities page? */
export const isCapabilitiesPath = (path: string): boolean => path.toLowerCase() === CAPABILITIES_PAGE_PATH

/** The app path of a person's page: by user id, so a handle change never breaks the link. */
export const personPagePathFor = (workspaceId: string, userId: string, v?: number): string =>
  `/w/${enc(workspaceId)}/knowledge/people/${enc(userId)}${v ? `?v=${v}` : ""}`

/** The app path of a guideline's page. */
export const guidelinePagePathFor = (workspaceId: string, topic: string, v?: number): string =>
  `/w/${enc(workspaceId)}/knowledge/guidelines/${enc(topic)}${v ? `?v=${v}` : ""}`

/**
 * The app path of a workspace page by its workspace path (`people/jannik.md`, `guidelines/stack.md`), or null. People
 * need their user id, looked up in the list.
 */
export const workspacePageHref = (
  workspaceId: string,
  path: string,
  pages: ReadonlyArray<{ readonly path: string; readonly userId: string | null }>,
): string | null => {
  const topic = workspaceTopicOf(path)
  if (topic) return guidelinePagePathFor(workspaceId, topic)
  if (isCapabilitiesPath(path)) return capabilitiesPagePathFor(workspaceId)
  if (personHandleOf(path) === null) return null
  const key = path.toLowerCase()
  const userId = pages.find((p) => p.path.toLowerCase() === key)?.userId
  return userId ? personPagePathFor(workspaceId, userId) : null
}

/** A topic slug from what someone typed: "Code review" → "code-review". Empty when nothing usable is left. */
export const topicSlug = (input: string): string =>
  input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\.md$/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "")

export const isTopic = (topic: string): boolean => WORKSPACE_TOPIC.test(topic)

/** "code-review" → "Code review". */
export const topicTitle = (topic: string): string => {
  const words = topic.replace(/-/g, " ").trim()
  return words ? `${words[0]?.toUpperCase() ?? ""}${words.slice(1)}` : topic
}

/** A new guideline's starting text. */
export const GUIDELINE_TEMPLATE = (topic: string): string =>
  [
    `# ${topicTitle(topic)}`,
    "",
    "What holds for every project in this workspace.",
    "",
    "## Rules",
    "",
    "- ",
    "",
  ].join("\n")

/** The workspace roles as people read them. */
export const ROLE_WORD: Record<PersonFrontmatter["role"], string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
}

/** Who wrote a version: a person's name, "Minga, from the account" for the people sync, else a plain word. */
export const actorName = (
  by: Actor,
  nameOf: (userId: string) => string | undefined,
  meId: string | null,
): string => {
  switch (by._tag) {
    case "user":
      return by.userId === meId ? "you" : (nameOf(by.userId) ?? "a person")
    case "system":
      return by.process === "people" ? "Minga, from the account" : "Minga"
    case "agent":
    case "worker":
      return "an agent"
    case "runner":
      return "a runner"
  }
}

/** "v4 · Updated by Becky · 2 d ago" (v1: "Written by"). */
export const versionByline = (
  page: Pick<WorkspacePageVersion, "n" | "by" | "updatedAt">,
  nameOf: (userId: string) => string | undefined,
  meId: string | null,
  now: number,
): string =>
  `v${page.n} · ${page.n === 1 ? "Written" : "Updated"} by ${actorName(page.by, nameOf, meId)} · ${formatRelative(new Date(page.updatedAt), now)}`

/** "Mar 2026" from `memberSince` (`YYYY-MM-DD`); the raw value when it doesn't parse. */
export const memberSinceText = (memberSince: string): string => {
  const at = Date.parse(`${memberSince}T00:00:00Z`)
  return Number.isNaN(at)
    ? memberSince
    : new Date(at).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
}

/**
 * The person hasn't written anything yet: the body has no text beyond its headings (the template's `# Name` and the
 * `## About … ## Don't` sections), or "None." placeholders.
 */
export const isEmptyPersonBody = (body: string): boolean =>
  body
    .split("\n")
    .map((l) => l.trim())
    .every((l) => l === "" || /^#{1,6}\s/.test(l) || /^none\.?$/i.test(l) || l === "-")

/** The `## …` sections a person page starts with, for the empty state's hint. */
export const PERSON_SECTION_HINT = PERSON_SECTIONS.join(", ")

/** The name to show for a person entry while its page loads: the page title without "# ". */
export const entryName = (entry: Pick<WorkspaceKnowledgeEntry, "title" | "path">): string =>
  entry.title.trim() || personHandleOf(entry.path) || entry.path

/** `@jannik` from `people/jannik.md`. */
export const entryHandle = (path: string): string | null => personHandleOf(path)

/** People first by name, guidelines by path, the runners pages (the capabilities page). */
export const sortEntries = (
  entries: ReadonlyArray<WorkspaceKnowledgeEntry>,
): {
  readonly people: ReadonlyArray<WorkspaceKnowledgeEntry>
  readonly guidelines: ReadonlyArray<WorkspaceKnowledgeEntry>
  readonly runners: ReadonlyArray<WorkspaceKnowledgeEntry>
} => {
  const live = entries.filter((e) => !e.archived)
  return {
    people: live.filter((e) => e.kind === "person").sort((a, b) => entryName(a).localeCompare(entryName(b))),
    guidelines: live
      .filter((e) => e.kind === "workspace_guideline")
      .sort((a, b) => a.path.localeCompare(b.path)),
    runners: live.filter((e) => e.kind === "capabilities"),
  }
}

/** The text the editor starts from: a person's body (the frontmatter is the account's), a guideline's whole text. */
export const editableText = (page: Pick<WorkspacePageVersion, "kind" | "text" | "body">): string =>
  page.kind === "person" ? page.body : page.text

/**
 * Who may change this page here: the person on their own page; owner and admins on guidelines and (decision 0100) the
 * capabilities page, which the leads keep too.
 */
export const canEditPage = (
  page: Pick<WorkspacePageVersion, "kind" | "userId" | "isHead" | "archived">,
  meId: string | null,
  canWriteGuidelines: boolean,
): boolean => {
  if (!page.isHead || page.archived) return false
  return page.kind === "person" ? meId !== null && page.userId === meId : canWriteGuidelines
}
